import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Collapse, IconButton } from '@mui/material';
import clsx from 'clsx';
import CustomDrawer from 'components/CustomDrawer';
import useReplayTour from 'components/V2/SidebarTour/hooks/useReplayTour';
import { IS_SANDBOX_ENV } from 'helper/constant';
import SetupGuideModal from 'layouts/V2/Dashboard/components/SetupGuideModal';
import PropTypes from 'prop-types';

import { selectIsSetupGuideActive } from 'redux/selectors/general';

import styles from '../sidebar.module.css';

import { ReactComponent as ChevronRight } from 'asset/IconsPack/at_chevron-right-filled.svg';
import { ReactComponent as HelpCircleIcon } from 'asset/IconsPack/at_help-circle.svg';
import { ReactComponent as CloseIcon } from 'asset/IconsPack/at_x-close.svg';

import AccountMenu from './AccountMenu';
import BrandHeader from './BrandHeader';
import HelpMenu from './HelpMenu';
import NavigationList from './NavigationList';

function SmallScreenSidebar({ openSidebar, handleToggler, isMobile, handleHelpClick }) {
    const isSetupGuideActive = useSelector(selectIsSetupGuideActive);
    const [openSetupGuideModal, setOpenSetupGuideModal] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const replayTour = useReplayTour();

    const handleSetupGuide = () => {
        setOpenSetupGuideModal(true);
    };

    // The tour points at the page bar, so the drawer closes first.
    const handleReplayTour = () => {
        handleToggler();
        replayTour();
    };

    const handleCloseSetupGuide = () => {
        setOpenSetupGuideModal(false);
        handleToggler();
    };

    // Picking a page closes the drawer, including Settings links that only change the hash.
    const handleNavClick = (event) => {
        if (event.target.closest('a')) {
            handleToggler();
        }
    };

    return (
        <>
            <CustomDrawer
                anchor="left"
                variant="temporary"
                className={clsx(styles.tabletSidebar, isMobile && styles.mobileSidebar)}
                open={openSidebar}
                onClose={handleToggler}
            >
                <Box className={styles.drawerHeader}>
                    <BrandHeader className={styles.drawerBrand} />
                    <IconButton
                        onClick={handleToggler}
                        className={styles.drawerCloseBtn}
                        aria-label="Close menu"
                    >
                        <CloseIcon className={styles.drawerCloseIcon} />
                    </IconButton>
                </Box>
                <Box className={styles.drawerNavList} onClick={handleNavClick}>
                    <NavigationList />
                </Box>
                {!isMobile && (
                    <Box className={styles.sidebarFooter} onClick={handleNavClick}>
                        <HelpMenu
                            handleHelpClick={handleHelpClick}
                            onReplayTour={handleReplayTour}
                        />
                        <AccountMenu />
                    </Box>
                )}
                {isMobile && (
                    <Box className={styles.sidebarFooter} onClick={handleNavClick}>
                        <button
                            type="button"
                            className={clsx(styles.navItem, styles.navItemButton)}
                            onClick={() => setIsHelpOpen(!isHelpOpen)}
                            aria-expanded={isHelpOpen}
                            aria-controls="help-sub-nav"
                        >
                            <span className={styles.navIcon}>
                                <HelpCircleIcon />
                            </span>
                            <span className={styles.navLabel}>Help & resources</span>
                            <ChevronRight
                                className={clsx(
                                    styles.navChevron,
                                    isHelpOpen && styles.navChevronOpen
                                )}
                                aria-hidden="true"
                            />
                        </button>
                        <Collapse in={isHelpOpen} id="help-sub-nav">
                            <Box className={styles.subNav}>
                                <a
                                    href={
                                        IS_SANDBOX_ENV
                                            ? 'https://info.authenticate.com/sandbox'
                                            : 'https://docs.authenticate.com/'
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    className={styles.subNavItem}
                                >
                                    Technical Docs
                                </a>
                                {!IS_SANDBOX_ENV && !isSetupGuideActive && (
                                    <button
                                        type="button"
                                        className={clsx(styles.subNavItem, styles.navItemButton)}
                                        onClick={handleSetupGuide}
                                    >
                                        Setup Guide
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={clsx(styles.subNavItem, styles.navItemButton)}
                                    onClick={handleReplayTour}
                                >
                                    Take the sidebar tour
                                </button>
                                <a
                                    href="https://info.authenticate.com/kb-tickets/new"
                                    target="_blank"
                                    rel="noreferrer"
                                    className={styles.subNavItem}
                                >
                                    Raise a Support Ticket
                                </a>
                            </Box>
                        </Collapse>
                        <AccountMenu inline />
                    </Box>
                )}
            </CustomDrawer>
            <SetupGuideModal open={openSetupGuideModal} onClose={handleCloseSetupGuide} />
        </>
    );
}

SmallScreenSidebar.propTypes = {
    isMobile: PropTypes.bool,
    openSidebar: PropTypes.bool,
    handleToggler: PropTypes.func,
    handleHelpClick: PropTypes.func,
};

export default SmallScreenSidebar;
